import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import interact from "interactjs";
import { getBrokenLinkUrl } from "../../common/utils";
import { useEditorLayerTime } from "../AtomicBoxPlugin/contexts/editorLayerTimes";
import RecorderCanvasImage from "./visor/RecorderCanvasImage";
import { useTimelinePreview } from "./useTimelinePreview";
/* eslint-disable react/prop-types */

// Cache ratios by URL so re-mounts have it on the first render (no flash).
const ratioCache = new Map();

export default function Image({ props, state, markElements }) {
  const brokenLinkUrl = getBrokenLinkUrl();
  const [hasImageError, setHasImageError] = useState(false);
  // Safari sizes a max-content box from the image's natural px (ignoring height),
  // overflowing it; for those boxes derive width from height via aspect-ratio.
  const [naturalRatio, setNaturalRatio] = useState(() => ratioCache.get(state.url) || null);
  const [autoWidthBox, setAutoWidthBox] = useState(false);
  // Frame-accurate canvas decode failed (CORS/timeout/no ImageDecoder) — stay
  // on the plain <img> path below instead of retrying every render.
  const [webpDecodeFailed, setWebpDecodeFailed] = useState(false);

  const draggableImageRef = useRef(null);
  const imgRef = useRef(null);

  const prevViewRef = useRef(null);
  const prevShowRef = useRef(null);

  useEffect(() => {
    const currView = props && props.currentView;
    const currShow = props && props.show;
    const viewChanged = JSON.stringify(prevViewRef.current) !== JSON.stringify(currView);
    const showBecameTrue = !prevShowRef.current && currShow;

    if (viewChanged || showBecameTrue) {
      setHasImageError(false);
    }

    prevViewRef.current = currView;
    prevShowRef.current = currShow;
  });

  const getImageSrc = useCallback(
    (url) => {
      if (!url || hasImageError) return brokenLinkUrl;
      return url;
    },
    [brokenLinkUrl, hasImageError]
  );

  // Interact.js drag mantığını useEffect ile kur
  useEffect(() => {
    if (!draggableImageRef.current) return;

    let scale = props.state ? props.state.scale || 1 : state.scale || 1;

    const interactable = interact(draggableImageRef.current).draggable({
      enabled: true,
      ignoreFrom: "a, .pointerEventsEnabled, .markeditor",
      onstart: (event) => {
        event.stopPropagation();
        const parent = event.target;
        const original = parent.childNodes[0];
        scale = state.scale || 1;

        const clone = original.cloneNode(true);
        const originalRect = original.getBoundingClientRect();
        const parentRect = parent.getBoundingClientRect();
        // Canvas zoom factor: bounding rect is in (scaled) screen px, the
        // translate transform lives in the box's own unscaled coordinate space.
        // Divide screen offsets by k so the drag tracks the cursor 1:1 at any
        // zoom level (was only correct at 100%).
        const k = parent.offsetWidth ? parentRect.width / parent.offsetWidth : 1;
        const x = (originalRect.left - parentRect.left) / k;
        const y = (originalRect.top - parentRect.top) / k;

        clone.setAttribute("id", "clone2");
        clone.setAttribute("data-x", x);
        clone.setAttribute("data-y", y);
        clone.style.zIndex = "9999 !important";
        original.setAttribute("data-x", x);
        original.setAttribute("data-y", y);
        clone.style.position = "absolute";
        clone.style.webkitTransform =
          clone.style.MozTransform =
          clone.style.msTransform =
          clone.style.OTransform =
            `translate(${x}px, ${y}px) scale(${scale})`;
        parent.appendChild(clone);
        original.style.opacity = 0;
      },
      onmove: (event) => {
        event.stopPropagation();
        scale = state.scale || 1;
        const target = document.getElementById("clone2");
        const original = event.target.childNodes[0];
        // Convert the screen-space pointer delta into the box's unscaled space.
        const k = event.target.offsetWidth
          ? event.target.getBoundingClientRect().width / event.target.offsetWidth
          : 1;
        const x = (parseFloat(target.getAttribute("data-x")) || 0) + event.dx / k;
        const y = (parseFloat(target.getAttribute("data-y")) || 0) + event.dy / k;
        target.style.webkitTransform =
          target.style.MozTransform =
          target.style.msTransform =
          target.style.OTransform =
            `translate(${x}px, ${y}px) scale(${scale})`;
        target.style.zIndex = "9999";
        target.setAttribute("data-x", x);
        target.setAttribute("data-y", y);
        original.setAttribute("data-x", x);
        original.setAttribute("data-y", y);
      },
      onend: (event) => {
        event.stopPropagation();
        const target = event.target.childNodes[0];
        scale = state.scale || 1;
        target.style.zIndex = "0";
        target.style.opacity = 1;
        const actualLeft = target.getAttribute("data-x");
        const actualTop = target.getAttribute("data-y");
        const x = (parseFloat(actualLeft) * 100) / target.offsetWidth;
        const y = (parseFloat(actualTop) * 100) / target.offsetHeight;
        const clone = document.getElementById("clone2");
        if (clone) clone.parentElement.removeChild(clone);
        props.update("translate", { x, y });
        event.stopPropagation();
      },
    });

    return () => {
      interactable.unset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draggableImageRef.current]);

  const scale = state.scale || 1;
  const translateX = (state.translate ? state.translate.x : 0) || 0;
  const translateY = (state.translate ? state.translate.y : 0) || 0;
  const transform = `translate(${translateX}%,${translateY}%) scale(${scale})`;
  const imageSrc = hasImageError ? brokenLinkUrl : getImageSrc(state.url);

  // A new url (e.g. re-animated image) deserves a fresh decode attempt.
  useEffect(() => {
    setWebpDecodeFailed(false);
  }, [state.url]);

  // Timeline scrub/play feeds a live per-box clock (editorLayerTimes, set by
  // EditorApp's updateAtomicLayerCurrentTime). No entry means the box isn't currently under
  // the playhead, or the timeline has never been touched — stay on the plain
  // <img> (wall-clock) path below.
  const timelinePreview = useTimelinePreview();
  const boxId = props && props.id;
  const editorLayerTime = useEditorLayerTime(boxId);
  const editorCurrentTimeSec = typeof editorLayerTime === "number" ? editorLayerTime : null;
  const isAnimatedFormat =
    typeof state.url === "string" && /(\.webp|\.gif|\.apng|\.avif)(\?|#|$)/i.test(state.url);
  const useFrameAccurateCanvas =
    isAnimatedFormat && !hasImageError && !webpDecodeFailed && editorCurrentTimeSec !== null;

  // Measure intrinsic ratio + detect a max-content box before paint (no flash).
  // SVGs use a detached probe — Safari's in-DOM naturalWidth for them is wrong.
  useLayoutEffect(() => {
    const img = imgRef.current;
    const box = img && img.closest(".wholebox, .wholeboxvisor");
    setAutoWidthBox(!!box && box.style.width === "max-content");

    const applyRatio = (w, h) => {
      if (!w || !h) return;
      const r = w / h;
      ratioCache.set(state.url, r);
      setNaturalRatio(r);
    };

    const isSvg = /\.svg(\?|#|$)/i.test(state.url || "");
    if (!isSvg && img && img.complete && img.naturalWidth && img.naturalHeight) {
      applyRatio(img.naturalWidth, img.naturalHeight);
      return undefined;
    }

    if (!imageSrc) return undefined;
    // window.Image: this component is named Image, shadowing the global ctor.
    const probe = new window.Image();
    const read = () => applyRatio(probe.naturalWidth, probe.naturalHeight);
    probe.onload = read;
    probe.src = imageSrc;
    if (probe.complete) read();
    return () => {
      probe.onload = null;
    };
  }, [imageSrc]);

  return (
    <div
      style={{ height: "100%", width: "100%" }}
      className="draggableImage"
      ref={draggableImageRef}
      onWheel={(e) => {
        const delta = Math.sign(e.deltaY);
        props.update("scale", Math.round(10 * Math.min(20, Math.max(0, scale - delta / 10))) / 10);
      }}
    >
      {useFrameAccurateCanvas ? (
        <RecorderCanvasImage
          url={state.url}
          visorFrameTime={editorCurrentTimeSec * 30}
          boxStartFrame={0}
          fps={30}
          playing={timelinePreview.advancing}
          seekId={timelinePreview.seekId}
          onDecodeFailed={() => setWebpDecodeFailed(true)}
          imgStyle={{
            height: autoWidthBox ? "100%" : state.allowDeformed ? "" : "auto",
            transform,
            WebkitTransform: transform,
            MozTransform: transform,
          }}
        />
      ) : (
        <img
          ref={imgRef}
          id={props.id + "-image"}
          className="basicImageClass"
          style={{
            overflow: "hidden",
            width: autoWidthBox ? "auto" : "100%",
            height: autoWidthBox ? "100%" : state.allowDeformed ? "" : "auto",
            aspectRatio: autoWidthBox && naturalRatio ? naturalRatio : undefined,
            transform,
            WebkitTransform: transform,
            MozTransform: transform,
          }}
          src={imageSrc}
          onError={(e) => {
            e.target.onerror = null;
            setHasImageError(true);
          }}
        />
      )}
      <div
        className="dropableRichZone"
        style={{
          overflow: "hidden",
          height: "100%",
          width: "100%",
          position: "absolute",
          top: 0,
          left: 0,
        }}
      >
        {markElements}
      </div>
    </div>
  );
}
/* eslint-enable react/prop-types */
